// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Fragment, createRef } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faPlug } from '@fortawesome/free-solid-svg-icons'

import {
  Card,
  ColumnGrid,
  NavLink,
  SvgIcon,
  EmployerPayslipFileUploader,
  Modal,
  Reporting,
  EmployerName,
  EmployerEmployeeAdd,
  Feedback,
  EmployeeCSVImporter,
  EmployerGroupAdd,
  EmployerAdd,
  DivisionAdd,
  UserAdd,
  PayslipAPIAccessManager,
  SMSAPIAccessManager
} from '../components'

import { hasResponsibility } from '../utils'

import { AccountContext } from '../context'

import { EMPLOYER_MESSAGEPLUS } from '../constants'

import backgroundImage from '../assets/img/dash-bg.jpg'

const RIGHT_ARROW = <span style={{ fontSize: 20 }}>&rarr;</span>

const modalRef = createRef()


export default class Dashboard extends React.Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    refetch: PropTypes.bool
  }

  state = {
    refetch: false,
    showPayslipFileUploadModal: false,
    showEmployeeAddModal: false,
    showEmployeeImportModal: false,
    showAddSuccess: false,
    showEmployerGroupAddModal: false,
    showEmployerAddModal: false,
    showDivisionAddModal: false,
    showUserAddModal: false,
    showAPIModal: false
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      // click is outside, close
      this.setState({
        showPayslipFileUploadModal: false,
        showEmployeeAddModal: false,
        showEmployeeImportModal: false,
        showEmployerGroupAddModal: false,
        showEmployerAddModal: false,
        showDivisionAddModal: false,
        showUserAddModal: false,
        showAPIModal: false
      })
    }
  }

  togglePayslipFileUploadModal = () => (
    this.setState(prevState => ({
      showPayslipFileUploadModal: !prevState.showPayslipFileUploadModal
    }))
  )

  toggleEmployeeAddModal = () => (
    this.setState(prevState => ({
      showEmployeeAddModal: !prevState.showEmployeeAddModal
    }))
  )

  toggleEmployeeImportModal = () => (
    this.setState(prevState => ({
      showEmployeeImportModal: !prevState.showEmployeeImportModal
    }))
  )

  toggleEmployerGroupAddModal = () => (
    this.setState(prevState => ({
      showEmployerGroupAddModal: !prevState.showEmployerGroupAddModal
    }))
  )

  toggleEmployerAddModal = () => (
    this.setState(prevState => ({
      showEmployerAddModal: !prevState.showEmployerAddModal
    }))
  )

  toggleDivisionAddModal = () => (
    this.setState(prevState => ({
      showDivisionAddModal: !prevState.showDivisionAddModal
    }))
  )

  toggleUserAddModal = () => (
    this.setState(prevState => ({
      showUserAddModal: !prevState.showUserAddModal
    }))
  )

  showAPIModal = (api) => (
    this.setState({ showAPIModal: api })
  )

  render() {
    const { showPayslipFileUploadModal, showEmployeeAddModal, showAddSuccess, showEmployeeImportModal,
      showEmployerGroupAddModal, showEmployerAddModal, showDivisionAddModal, showUserAddModal, showAPIModal } = this.state

    return (
      <AccountContext.Consumer>
        {({ currentAccountId, isEmployerGroupAccount }) => {
          const urlHead = isEmployerGroupAccount
            ? `/group/${currentAccountId}`
            : `/employer/${currentAccountId}`

          const userHasPayslipUploadPermission = hasResponsibility('self', 'messageplus_payslip_upload') && !isEmployerGroupAccount

          return (
            <div
              className='background-image_cover'
              style={{ backgroundImage: `url(${backgroundImage})`, height: '100vh' }}>

              <ColumnGrid
                items={[
                  [
                    <RecentActivity />,
                    <Reporting isEmployerGroupAccount={isEmployerGroupAccount} />,
                    // currentAccountId === EMPLOYER_MESSAGEPLUS &&
                    <API onClick={api => this.showAPIModal(api)} />
                  ],
                  [
                    <Card
                      title='People'
                      icon={
                        <SvgIcon
                          variant='picsaos'
                          symbol='people'
                          colour='grey'
                          size='medium'
                        />
                      }
                      body={
                        <ul className='slds-p-bottom_medium'>
                          <li>
                            <div className='slds-border_bottom slds-p-bottom_small' style={{ lineHeight: 1.5 }}>
                              <div className='u-font-weight-bold u-font-size-larger'>
                                Manage
                              </div>
                              <div className='clickable clickable-link slds-grid slds-p-top_x-small' style={{ gridAutoFlow: 'column', alignItems: 'center' }}>
                                <NavLink to={`${urlHead}/employees`}>
                                  See employee listing
                                </NavLink>
                                <NavLink to={`${urlHead}/employees`} className='slds-col_bump-left'>
                                  {RIGHT_ARROW}
                                </NavLink>
                              </div>
                              <div
                                onClick={isEmployerGroupAccount
                                  ? () => null  // TODO: figure out how we want to allow new employee addition for employer group account
                                  : this.toggleEmployeeAddModal
                                }
                                className={`${isEmployerGroupAccount ? '' : 'clickable clickable-link'} slds-grid slds-p-top_xxx-small`}
                                style={isEmployerGroupAccount
                                  ? { color: 'lightgrey', gridAutoFlow: 'column', alignItems: 'center' }
                                  : { gridAutoFlow: 'column', alignItems: 'center' }
                                }>
                                <div>
                                  Add new employee
                                </div>
                                <div className='slds-col_bump-left'>
                                  {RIGHT_ARROW}
                                </div>
                              </div>
                              {hasResponsibility('self','messageplus_employee_csv_import') &&
                                <div
                                  onClick={isEmployerGroupAccount
                                    ? () => null  // TODO: figure out how we want to allow employee import for employer group account
                                    : this.toggleEmployeeImportModal
                                  }
                                  className={`${isEmployerGroupAccount ? '' : 'clickable clickable-link'} slds-grid slds-p-top_xxx-small`}
                                  style={isEmployerGroupAccount
                                    ? { color: 'lightgrey', gridAutoFlow: 'column', alignItems: 'center' }
                                    : { gridAutoFlow: 'column', alignItems: 'center' }
                                  }>
                                  <div>
                                    Import multiple employees using CSV file
                                  </div>
                                  <div className='slds-col_bump-left'>
                                    {RIGHT_ARROW}
                                  </div>
                                </div>
                              }
                            </div>
                          </li>
                          <li className='slds-p-top_medium'>
                            <div className='slds-border_bottom slds-p-bottom_small' style={{ lineHeight: 1.5 }}>
                              <div className='u-font-weight-bold u-font-size-larger'>
                                Communicate
                              </div>
                              <div className='clickable clickable-link slds-grid slds-p-top_x-small' style={{ gridAutoFlow: 'column', alignItems: 'center' }}>
                                <NavLink to={`${urlHead}/communications/bulk`}>
                                  Bulk Messaging
                                </NavLink>
                                <NavLink to={`${urlHead}/communications/bulk`} className='slds-col_bump-left'>
                                  {RIGHT_ARROW}
                                </NavLink>
                              </div>
                              <div className='clickable clickable-link slds-grid slds-p-top_xxx-small' style={{ gridAutoFlow: 'column', alignItems: 'center' }}>
                                <NavLink to={`${urlHead}/communications/whatsapp`}>
                                  WhatsApp Messaging
                                </NavLink>
                                <NavLink to={`${urlHead}/communications/whatsapp`} className='slds-col_bump-left'>
                                  {RIGHT_ARROW}
                                </NavLink>
                              </div>
                            </div>
                          </li>
                          {currentAccountId === EMPLOYER_MESSAGEPLUS &&
                            <li className='slds-p-top_medium'>
                              <div className='slds-border_bottom slds-p-bottom_small' style={{ lineHeight: 1.5 }}>
                                <div className='u-font-weight-bold u-font-size-larger'>
                                  Admin
                                </div>
                                {true &&  // TODO: add permission
                                  <div
                                    onClick={this.toggleEmployerGroupAddModal}
                                    className='clickable clickable-link slds-grid slds-p-top_x-small'
                                    style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
                                    Add employer group
                                    <div className='slds-col_bump-left'>
                                      {RIGHT_ARROW}
                                    </div>
                                  </div>
                                }
                                {true &&  // TODO: add permission
                                  <div
                                    onClick={this.toggleEmployerAddModal}
                                    className='clickable clickable-link slds-grid slds-p-top_xxx-small'
                                    style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
                                    Add employer
                                    <div className='slds-col_bump-left'>
                                      {RIGHT_ARROW}
                                    </div>
                                  </div>
                                }
                                {true &&  // TODO: add permission
                                  <div
                                    onClick={this.toggleDivisionAddModal}
                                    className='clickable clickable-link slds-grid slds-p-top_xxx-small'
                                    style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
                                    Add division
                                    <div className='slds-col_bump-left'>
                                      {RIGHT_ARROW}
                                    </div>
                                  </div>
                                }
                                {true &&  // TODO: add permission
                                  <div
                                    onClick={this.toggleUserAddModal}
                                    className='clickable clickable-link slds-grid slds-p-top_xxx-small'
                                    style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
                                    Add user
                                    <div className='slds-col_bump-left'>
                                      {RIGHT_ARROW}
                                    </div>
                                  </div>
                                }
                              </div>
                            </li>
                          }
                          <li className='slds-p-top_medium'>
                            <div className='slds-border_bottom slds-p-bottom_small' style={{ lineHeight: 1.5 }}>
                              <div className='u-font-weight-bold u-font-size-larger'>
                                Payroll
                              </div>
                              <div
                                onClick={userHasPayslipUploadPermission
                                  ? this.togglePayslipFileUploadModal
                                  : null
                                }
                                className={`${userHasPayslipUploadPermission ? 'clickable clickable-link ' : ''}slds-grid slds-p-top_x-small`}
                                style={{ color: userHasPayslipUploadPermission ? 'inherit' : 'lightgrey', gridAutoFlow: 'column', alignItems: 'center' }}>
                                Upload payslip file(s)
                                <SvgIcon
                                  variant='utility'
                                  symbol='upload'
                                  size='xx-small'
                                  colour={userHasPayslipUploadPermission ? '#9644fb' : 'lightgrey'}
                                  className='slds-col_bump-left'
                                />
                              </div>
                            </div>
                          </li>
                        </ul>
                      }
                    />
                  ]
                ]}
              />
              {showPayslipFileUploadModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <EmployerPayslipFileUploader
                      employerId={currentAccountId}
                      title={
                        <Fragment>
                          Choose payslips files to upload for <EmployerName id={currentAccountId} style={{ color: 'rgb(90, 86, 251)' }}/>:
                        </Fragment>
                      }
                      onCancel={this.togglePayslipFileUploadModal}
                      onSuccess={this.togglePayslipFileUploadModal}
                    />
                  }
                />
              }
              {showEmployeeAddModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <EmployerEmployeeAdd
                      title='Add employee'
                      employerId={currentAccountId}
                      onCancel={this.toggleEmployeeAddModal}
                      onSuccess={() => {
                        this.toggleEmployeeAddModal()
                        this.setState({ showAddSuccess: true })
                      }}
                    />
                  }
                />
              }
              {showAddSuccess &&
                <Feedback
                  message='Added'
                  fadeDuration={2500}  // 2.5s matches .feedback-fade css class animation
                  unmount={() => this.setState({ showAddSuccess: false })}
                  icon={
                    <SvgIcon
                      variant='utility'
                      symbol='check'
                      colour='inverse'
                      className='slds-align_absolute-center'
                    />
                  }
                />
              }
              {showEmployeeImportModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <EmployeeCSVImporter
                      employerId={currentAccountId}
                    />
                  }
                />
              }
              {showEmployerGroupAddModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <EmployerGroupAdd
                      title='Add employer group'
                      onCancel={this.toggleEmployerGroupAddModal}
                      onSuccess={() => {
                        this.toggleEmployerGroupAddModal()
                        this.setState({ showAddSuccess: true })
                      }}
                    />
                  }
                />
              }
              {showEmployerAddModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <EmployerAdd
                      title='Add employer'
                      onCancel={this.toggleEmployerAddModal}
                      onSuccess={() => {
                        this.toggleEmployerAddModal()
                        this.setState({ showAddSuccess: true })
                      }}
                    />
                  }
                />
              }
              {showDivisionAddModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <DivisionAdd
                      title='Add division'
                      onCancel={this.toggleDivisionAddModal}
                      onSuccess={() => {
                        this.toggleDivisionAddModal()
                        this.setState({ showAddSuccess: true })
                      }}
                    />
                  }
                />
              }
              {showUserAddModal &&
                <Modal
                  ref={modalRef}
                  body={
                    <UserAdd
                      title='Add user'
                      onCancel={this.toggleUserAddModal}
                      onSuccess={() => {
                        this.toggleUserAddModal()
                        this.setState({ showAddSuccess: true })
                      }}
                    />
                  }
                />
              }
              {showAPIModal === 'sms' &&
                <Modal
                  ref={modalRef}
                  heading='Manage API access'
                  subHeading={
                    <div>
                      <b>API endpoint(s):</b>
                      <ul style={{ listStyle: 'disc' }}>
                        <li>
                          api/sms/send/<i className='slds-text-color_weak'>id_number</i>/
                        </li>
                      </ul>
                    </div>
                  }
                  body={
                    <SMSAPIAccessManager />
                  }
                />
              }
              {showAPIModal === 'payslips' &&
                <Modal
                  ref={modalRef}
                  heading='Manage API access'
                  subHeading={
                    <div>
                      <b>API endpoint(s):</b>
                      <ul style={{ listStyle: 'disc' }}>
                        <li>
                          api/payslips/<i className='slds-text-color_weak'>id_number</i>/<i className='slds-text-color_weak'>amount</i>/
                        </li>
                      </ul>
                    </div>
                  }
                  body={
                    <PayslipAPIAccessManager />
                  }
                />
              }
            </div>
          )
        }}
      </AccountContext.Consumer>
    )
  }
}


const RecentActivity = () => (
  <Card
    title='Recent activity'
    icon={
      <SvgIcon
        variant='utility'
        symbol='feed'
        size='medium'
      />
    }
    info={null}
    body={
      <div className='slds-p-bottom_medium u-font-size-larger'>
        Coming soon
      </div>
    }
    footer={null}
    yScrollable={true}
    paddingTRL={true}
  />
)

const API = ({ onClick }) => (
  <Card
    title='API'
    icon={
      <Icon icon={faPlug} transform="grow-15" className='slds-m-left_x-small slds-m-right_x-small' />
    }
    body={
      <ul className='slds-p-bottom_medium'>
        <li>
          <div className='slds-border_bottom slds-p-bottom_small' style={{ lineHeight: 1.5 }}>
            <div className='slds-grid u-font-weight-bold u-font-size-larger'>
              Manage access
            </div>
            <div
              onClick={() => onClick('sms')}
              className='clickable clickable-link slds-grid slds-p-top_x-small'
              style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
              SMS
              <div className='slds-col_bump-left'>
                {RIGHT_ARROW}
              </div>
            </div>
            <div
              onClick={() => onClick('payslips')}
              className='clickable clickable-link slds-grid slds-p-top_xxx-small'
              style={{ color: 'inherit', gridAutoFlow: 'column', alignItems: 'center' }}>
              Payslips
              <div className='slds-col_bump-left'>
                {RIGHT_ARROW}
              </div>
            </div>
          </div>
        </li>
      </ul>
    }
  />
)



// WEBPACK FOOTER //
// ./src/containers/Dashboard.js